import React, { useState } from "react";
import { 
  FiPlus, 
  FiThumbsUp, 
  FiCheckCircle, 
  FiGrid, 
  FiLayers, 
  FiAward, 
  FiTrash2,
  FiSend
} from "react-icons/fi";
import type { 
  BrainstormSession, 
  BrainstormIdea, 
  RcaMethodCode 
} from "../../../../types/rca.types.js";

export type BrainstormWorkspaceProps = {
  rcaRecordId?: string;
  problemStatement: string;
  readOnly?: boolean;
  onSave?: (session: BrainstormSession) => void;
  onChainMethod?: (method: RcaMethodCode, selectedIdea: string) => void;
  onRootCauseIdentified?: (rootCauseText: string, category: string, confidence: number) => void;
};

const INITIAL_IDEAS: BrainstormIdea[] = [
  { id: "idea-1", text: "Calibration drift on tool holder", authorName: "John Smith", color: "yellow", votesCount: 8, isShortlisted: true, clusterName: "Machine & Tooling" },
  { id: "idea-2", text: "Temperature variation on shop floor", authorName: "Kumar", color: "green", votesCount: 6, isShortlisted: true, clusterName: "Environment" },
  { id: "idea-3", text: "Sensor failure in Z-axis encoder", authorName: "Arun", color: "blue", votesCount: 5, isShortlisted: true, clusterName: "Machine & Tooling" },
  { id: "idea-4", text: "Lubrication pressure drop during high load", authorName: "Priya", color: "pink", votesCount: 2, isShortlisted: false, clusterName: "Maintenance" },
  { id: "idea-5", text: "Operator fatigue during night shift", authorName: "Ramesh", color: "orange", votesCount: 3, isShortlisted: false, clusterName: "Personnel" },
  { id: "idea-6", text: "Tool insert wear after 500 cycles", authorName: "John Smith", color: "purple", votesCount: 4, isShortlisted: false, clusterName: "Machine & Tooling" },
  { id: "idea-7", text: "Power fluctuation causing drive glitch", authorName: "Kiran", color: "yellow", votesCount: 1, isShortlisted: false, clusterName: "Electrical" },
  { id: "idea-8", text: "Material batch hardness variation", authorName: "Arun", color: "blue", votesCount: 2, isShortlisted: false, clusterName: "Material" },
];

const COLOR_STYLES: Record<string, string> = {
  yellow: "bg-amber-50 border-amber-300 text-amber-950",
  green: "bg-emerald-50 border-emerald-300 text-emerald-950",
  blue: "bg-blue-50 border-blue-300 text-blue-950",
  pink: "bg-rose-50 border-rose-300 text-rose-950",
  orange: "bg-orange-50 border-orange-300 text-orange-950",
  purple: "bg-purple-50 border-purple-300 text-purple-950",
};

export function BrainstormWorkspace({
  rcaRecordId = "rca-current",
  problemStatement,
  readOnly = false,
  onSave,
  onChainMethod,
  onRootCauseIdentified,
}: BrainstormWorkspaceProps) {
  const [activeTab, setActiveTab] = useState<"ideas" | "clusters" | "voting">("ideas");
  const [ideas, setIdeas] = useState<BrainstormIdea[]>(INITIAL_IDEAS);
  const [newIdeaText, setNewIdeaText] = useState<string>("");
  const [selectedColor, setSelectedColor] = useState<BrainstormIdea["color"]>("yellow");
  const [isVotingActive, setIsVotingActive] = useState<boolean>(true);

  const shortlisted = ideas.filter(i => i.isShortlisted || i.votesCount >= 5);

  const handleAddIdea = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newIdeaText.trim() || readOnly) return;
    const newIdea: BrainstormIdea = {
      id: `idea-${Date.now()}`,
      text: newIdeaText.trim(),
      authorName: "Current User",
      color: selectedColor,
      votesCount: 0,
      isShortlisted: false,
      clusterName: "General",
    };
    setIdeas(prev => [newIdea, ...prev]);
    setNewIdeaText("");
  };

  const handleVote = (id: string) => {
    if (readOnly || !isVotingActive) return;
    setIdeas(prev => prev.map(i => {
      if (i.id === id) {
        const nextVotes = i.votesCount + 1;
        return { ...i, votesCount: nextVotes, isShortlisted: nextVotes >= 5 };
      }
      return i;
    }));
  };

  const handleToggleShortlist = (id: string) => {
    if (readOnly) return;
    setIdeas(prev => prev.map(i => i.id === id ? { ...i, isShortlisted: !i.isShortlisted } : i));
  };

  const handleDelete = (id: string) => {
    if (readOnly) return;
    setIdeas(prev => prev.filter(i => i.id !== id));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex flex-wrap items-center justify-between gap-4 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-500 text-white flex items-center justify-center font-bold text-sm shadow-sm">
            💡
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              17. Brainstorming Workspace
              <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-800 border border-amber-200">
                Divergent Ideation & Clustering
              </span>
            </h2>
            <p className="text-xs text-slate-500">
              Collaborative brainstorming to capture all possible root cause hypotheses before voting and structured clustering.
            </p>
          </div>
        </div>

        {/* Tabs & Voting Toggle */}
        <div className="flex items-center gap-3">
          <div className="flex bg-slate-100 p-1 rounded-lg text-xs font-medium border border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab("ideas")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "ideas" 
                  ? "bg-white text-amber-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Ideas Board ({ideas.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("clusters")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "clusters" 
                  ? "bg-white text-amber-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Thematic Clusters
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("voting")}
              className={`px-3 py-1.5 rounded-md transition-all ${
                activeTab === "voting" 
                  ? "bg-white text-amber-700 shadow-sm font-bold" 
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              Voting Results ({shortlisted.length})
            </button>
          </div>

          <button
            type="button"
            onClick={() => setIsVotingActive(!isVotingActive)}
            className={`px-3.5 py-1.5 text-xs font-semibold rounded-lg shadow-sm transition-colors ${
              isVotingActive ? "bg-amber-600 text-white" : "bg-slate-200 text-slate-700"
            }`}
          >
            {isVotingActive ? "Voting Active" : "Start Voting"}
          </button>
        </div>
      </div>

      {/* Metrics Banner */}
      <div className="px-6 py-2.5 bg-amber-50/50 border-b border-amber-100 flex flex-wrap items-center justify-between gap-4 text-xs">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-slate-500">Total Ideas: </span>
            <span className="font-bold text-slate-900">{ideas.length}</span>
          </div>
          <div>
            <span className="text-slate-500">Shortlisted: </span>
            <span className="font-bold text-amber-700">{shortlisted.length}</span>
          </div>
        </div>

        {shortlisted.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const top = shortlisted[0]?.text || "Calibration drift";
              onRootCauseIdentified?.(top, "MACHINE", 85);
              onChainMethod?.("FISHBONE", top);
            }}
            className="px-3.5 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-xs shadow-sm transition-colors"
          >
            Convert Shortlist to Fishbone Causes →
          </button>
        )}
      </div>

      {/* Main Board */}
      <div className="p-6 space-y-4">
        {/* Quick Add Sticky Note Bar */}
        {!readOnly && (
          <form onSubmit={handleAddIdea} className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={newIdeaText}
              onChange={(e) => setNewIdeaText(e.target.value)}
              placeholder="Type an idea / hypothesis and press Enter..."
              className="flex-1 text-xs px-3 py-2 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-amber-500 font-medium"
            />

            <div className="flex items-center gap-1.5">
              {(["yellow", "green", "blue", "pink", "orange", "purple"] as const).map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className={`w-6 h-6 rounded-full border-2 transition-all ${
                    COLOR_STYLES[color]
                  } ${selectedColor === color ? "ring-2 ring-slate-800 scale-110" : "opacity-70"}`}
                />
              ))}
            </div>

            <button
              type="submit"
              className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold rounded-lg shadow-sm flex items-center gap-1.5 transition-colors"
            >
              <FiPlus className="w-4 h-4" /> Add Sticky
            </button>
          </form>
        )}

        {/* Sticky Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {ideas.map(idea => (
            <div
              key={idea.id}
              className={`p-4 rounded-xl border-2 shadow-sm flex flex-col justify-between min-h-[130px] transition-all hover:shadow-md ${
                COLOR_STYLES[idea.color] || COLOR_STYLES.yellow
              } ${idea.isShortlisted ? "ring-2 ring-amber-500 scale-[1.02]" : ""}`}
            >
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-[10px] opacity-70">
                  <span>{idea.clusterName || "Idea"}</span>
                  {idea.isShortlisted && (
                    <span className="font-bold flex items-center gap-0.5 text-amber-800">
                      <FiAward className="w-3 h-3" /> Shortlist
                    </span>
                  )}
                </div>
                <p className="text-xs font-semibold leading-snug">{idea.text}</p>
              </div>

              <div className="mt-3 pt-2 border-t border-current/10 flex items-center justify-between text-xs">
                <button
                  type="button"
                  onClick={() => handleVote(idea.id)}
                  disabled={readOnly || !isVotingActive}
                  className="flex items-center gap-1 font-bold text-[11px] px-2 py-0.5 rounded bg-white/70 hover:bg-white transition-colors border border-slate-200/40"
                >
                  <FiThumbsUp className="w-3 h-3" /> {idea.votesCount}
                </button>

                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => handleToggleShortlist(idea.id)}
                    disabled={readOnly}
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/50 hover:bg-white/90 transition-colors"
                  >
                    {idea.isShortlisted ? "★" : "☆"}
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={() => handleDelete(idea.id)}
                      className="opacity-50 hover:opacity-100 hover:text-rose-600 transition-opacity"
                    >
                      <FiTrash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
